import React from 'react';
import { FormattedMessage } from 'react-intl';
import MikaCard from '../../components/MikaCard/MikaCard';

interface AgreementCardGridProps {
    data: any[];
    onEdit: (agreement: any) => void;
}

const STATUS_VISUAL: Record<number, { color: string; bg: string }> = {
    1: { color: '#f59e0b', bg: '#fef3c7' },
    2: { color: '#3b82f6', bg: '#dbeafe' },
    3: { color: '#0891b2', bg: '#cffafe' },
    4: { color: '#22c55e', bg: '#dcfce7' },
    5: { color: '#ef4444', bg: '#fef2f2' },
};

const STATUS_I18N_KEY: Record<number, string> = {
    1: 'agreement.status.onHold',
    2: 'agreement.status.inProduction',
    3: 'agreement.status.inWarehouse',
    4: 'agreement.status.completed',
    5: 'agreement.status.canceled',
};

const getInitials = (name: string, lastName: string): string => {
    const f = (name || '').charAt(0).toUpperCase();
    const l = (lastName || '').charAt(0).toUpperCase();
    return (f + l) || '?';
};

const getAvatarColor = (name: string): string => {
    const colors = ['#3b82f6', '#8b5cf6', '#06b6d4', '#f59e0b', '#ef4444', '#22c55e', '#ec4899', '#6366f1'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
};

const AgreementCardGrid: React.FC<AgreementCardGridProps> = ({ data, onEdit }) => {
    return (
        <div className="ccg-grid">
            {data.length === 0 ? (
                <div className="ccg-empty">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5">
                        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
                    </svg>
                    <p><FormattedMessage id="agreement.list.noAgreementsFound" defaultMessage="No agreements found" /></p>
                </div>
            ) : (
                data.map((agreement) => {
                    const fullName = `${agreement.clientName || ''} ${agreement.clientLastName || ''}`.trim();
                    const initials = getInitials(agreement.clientName, agreement.clientLastName);
                    const avatarColor = getAvatarColor(fullName);
                    const statusId = agreement.agreementStatus?.value;
                    const visual = STATUS_VISUAL[statusId] || { color: '#94a3b8', bg: '#f1f5f9' };
                    const statusKey = STATUS_I18N_KEY[statusId];

                    return (
                        <MikaCard
                            key={agreement.id}
                            onClick={() => onEdit(agreement)}
                            header={
                                <div className="ccg-card-header">
                                    <div className="ccg-avatar" style={{ background: avatarColor }}>
                                        {initials}
                                    </div>
                                    <div className="ccg-card-name">
                                        <span className="ccg-name">{fullName || '\u2014'}</span>
                                        {agreement.project?.label && <span className="ccg-company">{agreement.project.label}</span>}
                                    </div>
                                    <span className="ccg-status-badge" style={{ color: visual.color, background: visual.bg }}>
                                        {statusKey
                                            ? <FormattedMessage id={statusKey} defaultMessage={agreement.agreementStatus?.label || '\u2014'} />
                                            : (agreement.agreementStatus?.label || '\u2014')}
                                    </span>
                                </div>
                            }
                            body={
                                <div className="ccg-card-body-content">
                                    <div className="ccg-info-grid">
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="agreement.list.column.client" defaultMessage="Client" /></span>
                                            <span className="ccg-info-value">{agreement.clientName || '\u2014'}</span>
                                        </div>
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="agreement.list.column.lastName" defaultMessage="Last name" /></span>
                                            <span className="ccg-info-value">{agreement.clientLastName || '\u2014'}</span>
                                        </div>
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="agreement.list.column.city" defaultMessage="City" /></span>
                                            <span className="ccg-info-value">{agreement.city || '\u2014'}</span>
                                        </div>
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="agreement.list.column.total" defaultMessage="Total" /></span>
                                            <span className="ccg-info-value">
                                                {agreement.total
                                                    ? <span style={{ color: '#1e40af', fontWeight: 600 }}>{Number(agreement.total).toFixed(2)}{'\u20AC'}</span>
                                                    : '\u2014'
                                                }
                                            </span>
                                        </div>
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="agreement.list.column.project" defaultMessage="Project" /></span>
                                            <span className="ccg-info-value">{agreement.project?.label || '\u2014'}</span>
                                        </div>
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="agreement.list.column.deliveryDate" defaultMessage="Delivery date" /></span>
                                            <span className="ccg-info-value">{agreement.productDates?.latestEndDate || '\u2014'}</span>
                                        </div>
                                        {agreement.user?.userName && (
                                            <div className="ccg-info-item ccg-info-full">
                                                <span className="ccg-info-label"><FormattedMessage id="agreement.list.column.agent" defaultMessage="Agent" /></span>
                                                <span className="ccg-info-value">{agreement.user.userName}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            }
                            footer={
                                <div className="ccg-card-footer">
                                    <span className="ccg-date">
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                                        </svg>
                                        {agreement.productDates?.latestEndDate || '\u2014'}
                                    </span>
                                    <div className="ccg-footer-actions">
                                        <FormattedMessage id="common.action.edit" defaultMessage="Edit">
                                            {(label) => (
                                                <button
                                                    className="ccg-action-btn"
                                                    onClick={(e) => { e.stopPropagation(); onEdit(agreement); }}
                                                    title={label as unknown as string}
                                                >
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                                                    </svg>
                                                </button>
                                            )}
                                        </FormattedMessage>
                                    </div>
                                </div>
                            }
                        />
                    );
                })
            )}
        </div>
    );
};

export default AgreementCardGrid;
